// IE 2026 09 30 / ODOO-343432: What-if Scorecard — чистые функции карточного вида вкладки сравнения (AttrCompare.vue, compare_layout 'cards').
// Данные — строки источника DS-PM-scorecard (бэк common_modules/pm_scorecard.py): строка = модель семейства (pm_id, title, is_current, is_branch,
// can_apply, not_recalculated / data_state, пять чисел). Метрики и «что лучше» — строки настроек Comparison rows (binding_a, better, weight,
// on_click_widget). Здесь: лучшее по метрике (ничьи, пустые значения), Δ к базе, вердикт одной строкой, видимость Apply, действие Apply,
// цель клика по числу, компактные числа, выключатель кэша бэка (правило 8). Без Vue / DOM / стора — тесты tests/unit/scorecard.test.mjs.
import { t } from '@/i18n'

export const SC_MAX_DEFAULT = 4
export const SC_FLAG_CACHE = 'scorecard_cache'      // perf-flags (localStorage / URL perf-scorecard_cache=false): OFF → бэку scorecard_cache_off

const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v)
const str = (v) => (v === null || v === undefined ? '' : String(v))

export function num(v) {
    if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null
    const n = typeof v === 'number' ? v : Number(v)
    return Number.isFinite(n) ? n : null
}

export function truthy(v) {
    if (typeof v === 'boolean') return v
    const s = str(v).trim().toLowerCase()
    return s === '1' || s === 'true' || s === 'yes' || s === 'on'
}

// метрики карточек из строк настроек (use !== false, есть binding_a)
export function scorecardMetrics(rows) {
    return (Array.isArray(rows) ? rows : [])
        .filter(r => isObj(r) && r.use !== false && r.binding_a)
        .map(r => ({ binding: r.binding_a, title: r.title || r.binding_a, better: r.better || '', weight: num(r.weight), on_click_widget: str(r.on_click_widget) }))
}

// лучшее значение метрики: индексы строк; better lower / higher; пустые не участвуют; меньше двух значений или все равны — лучших нет
export function bestOf(values, better) {
    if (better !== 'lower' && better !== 'higher') return []
    const vals = values.map(num)
    const known = vals.map((v, i) => [v, i]).filter(([v]) => v !== null)
    if (known.length < 2) return []
    if (known.every(([v]) => v === known[0][0])) return []
    const pick = better === 'lower' ? Math.min(...known.map(([v]) => v)) : Math.max(...known.map(([v]) => v))
    return known.filter(([v]) => v === pick).map(([, i]) => i)
}

// карта binding -> Set индексов лучших строк
export function bestByMetric(rows, metrics) {
    const out = {}
    for (const m of metrics || []) out[m.binding] = new Set(bestOf((rows || []).map(r => (isObj(r) ? r[m.binding] : null)), m.better))
    return out
}

// Δ к базе: abs и pct (pct — null при нулевой / пустой базе)
export function deltaToBase(value, base) {
    const v = num(value), b = num(base)
    if (v === null || b === null) return { abs: null, pct: null }
    return { abs: v - b, pct: b ? (100 * (v - b)) / Math.abs(b) : null }
}

// сравнение с базой по одной метрике: 1 — лучше, -1 — хуже, 0 — равно, null — не сравнить
export function cmpToBase(value, base, better) {
    const v = num(value), b = num(base)
    if (v === null || b === null || (better !== 'lower' && better !== 'higher')) return null
    if (v === b) return 0
    return (better === 'lower' ? v < b : v > b) ? 1 : -1
}

// компактное число: 1.2M, 450K, 12.5 (money — префикс $, знак — отдельно)
export function fmtCompact(v, { money = false, sign = false } = {}) {
    const n = num(v)
    if (n === null) return t('n/a')
    const a = Math.abs(n)
    let s
    if (a >= 1e9) s = (a / 1e9).toFixed(a >= 1e10 ? 0 : 1) + 'B'
    else if (a >= 1e6) s = (a / 1e6).toFixed(a >= 1e7 ? 0 : 1) + 'M'
    else if (a >= 1e4) s = (a / 1e3).toFixed(0) + 'K'
    else s = a.toLocaleString('en-US', { maximumFractionDigits: a >= 100 ? 0 : 1 })
    s = s.replace(/\.0([KMB])$/, '$1')
    const pre = n < 0 ? '−' : (sign && n > 0 ? '+' : '')
    return pre + (money ? '$' : '') + s
}

export const isMoneyMetric = (m) => /(\$|money)/i.test(str(m?.title) + ' ' + str(m?.binding))

// вердикт одной строкой: лучшая не-базовая карточка по числу метрик, где она лучше базы (ничья — больше взвешенный выигрыш, затем порядок);
// «Norm +5 days is better than Base on 3 of 5: Uncovered, $ −$1.2M; Excess, $ −$0.4M»
export function verdict(rows, metrics, baseIdx = 0, titleOf = (r) => str(r?.title)) {
    rows = Array.isArray(rows) ? rows : []
    metrics = (metrics || []).filter(m => m.better === 'lower' || m.better === 'higher')
    const base = rows[baseIdx]
    if (!base || rows.length < 2 || !metrics.length) return ''
    let best = null
    rows.forEach((r, i) => {
        if (i === baseIdx || !isObj(r)) return
        let wins = 0, losses = 0, score = 0, graded = 0
        const reasons = []
        for (const m of metrics) {
            const c = cmpToBase(r[m.binding], base[m.binding], m.better)
            if (c === null) continue
            graded++
            if (c > 0) {
                wins++
                score += m.weight ?? 1
                const d = deltaToBase(r[m.binding], base[m.binding])
                reasons.push({ m, d })
            } else if (c < 0) { losses++; score -= m.weight ?? 1 }
        }
        const cand = { i, r, wins, losses, score, graded, reasons }
        if (!best || wins > best.wins || (wins === best.wins && score > best.score)) best = cand
    })
    if (!best || !best.graded) return ''
    const baseTitle = titleOf(base) || t('Base')
    if (!best.wins) return t('No model beats {base} on any metric', { base: baseTitle })
    const top = [...best.reasons].sort((a, b) => Math.abs(b.d.pct ?? 0) - Math.abs(a.d.pct ?? 0)).slice(0, 2)
    const why = top.map(x => `${x.m.title} ${fmtCompact(x.d.abs, { money: isMoneyMetric(x.m), sign: true })}`).join('; ')
    return t('{model} is better than {base} on {wins} of {n}: {why}', { model: titleOf(best.r) || t('Model'), base: baseTitle, wins: best.wins, n: best.graded, why })
}

// ветка не пересчитана: числа слоя-родителя
export function notRecalculated(row) {
    return isObj(row) && (truthy(row.not_recalculated) || row.data_state === 'parent')
}

export function noData(row) {
    return isObj(row) && row.data_state === 'none'
}

// IE 2026 09 30 / ODOO-343432 (follow-up): источник чисел карточки (бэк: source = metric_data | marts, money_source = metric_data |
// avg_cost | purchase_price) — короткая метка бейджа и подсказка (англ. ключи, словари ru/uk)
export function sourceLabel(row) {
    const s = isObj(row) ? str(row.source) : ''
    if (s === 'marts') return t('Calc marts')
    if (s === 'metric_data') return t('Metric data')
    return ''
}

// IE 2026 09 30 / ODOO-343432 (второй прогон Bio): запас на дату остатков 0 при спросе в окне > 0 — это данные ветки (br4/br5 посчитаны до
// правки keep_open_balance_beyond_lt), числа верны, но карточке нужна подсказка пересчитать ветку
export function zeroStock(row) {
    return isObj(row) && num(row.stock_qty) === 0 && (num(row.consumption_qty) ?? 0) > 0
}

export function sourceTip(row, moneyBindings = ['uncovered_money', 'stock_money', 'excess_money']) {
    if (!isObj(row) || !str(row.source)) return ''
    const layer = str(row.version_layer) || str(row.version)
    const out = [row.source === 'marts'
        ? t('Source: calculation marts (stock balance and allocation turnover) of version {layer}.', { layer })
        : t('Source: metric data table of version {layer}.', { layer })]
    // IE 2026 09 30 / ODOO-343432 (follow-up 2, прогон Bio): определения чисел источника, дата остатков, основа DOS; деньги обоих источников — × себестоимость
    out.push(row.source === 'marts'
        ? t('Uncovered and short items: uncovered outgoing demand (OUT_uncovered) from the stock month on.')
        : t('Uncovered and short items: oos metric of all nodes (every uncovered allocation, all levels) outside the past horizon.'))
    const sd = str(row.stock_date)
    if (sd) out.push(t('Stock at {date} (last balance date on or before the stock date).', { date: sd }))
    else if (row.source === 'marts' && num(row.stock_qty) === null) out.push(t('No stock balance on or before the stock date: stock is n/a.'))
    out.push(t('Days of supply: stock quantity / daily sales demand quantity of the window.'))
    const ms = str(row.money_source)
    if (ms === 'avg_cost') out.push(t('Money: quantity × average cost; items without it at the purchase price.'))
    else if (ms === 'purchase_price') out.push(t('Money: quantity × purchase price of the Purchases map.'))
    if (moneyBindings.every(b => num(row[b]) === null)) out.push(t('No prices found: money numbers are n/a.'))
    if (notRecalculated(row)) out.push(t('The branch has no calculation of its own: numbers of the parent layer.'))
    if (zeroStock(row)) out.push(t('Stock is zero at the stock date while there is demand — recalculate the branch?'))
    return out.join('\n')
}

// кнопка Apply: ветка, право (can_apply от бэка), не роль «только чтение», настройка apply_button не выключена
export function canShowApply(row, { readonly = false, applyButton = true } = {}) {
    if (!isObj(row) || readonly === true || applyButton === false) return false
    return truthy(row.is_branch) && truthy(row.can_apply) && !!str(row.pm_id)
}

// действие Apply — та же команда, что пункт меню ярлыка модели (бэк branch.backend_method → write_guard); confirm — диалог подтверждения
export function applyAction(row, workspace = '') {
    const title = str(row?.title) || str(row?.pm_id)
    return {
        command: 'executeBackendMethod', method: 'apply_branch', title: t('Apply branch to parent'),
        params: { process_model: str(row?.pm_id), ...(workspace ? { workspace } : {}) },
        confirm: t('Apply branch «{title}» to its parent model? Edited and deleted records, recalculated documents and materialized view deltas of the branch will be written into the parent layer (on conflicts the branch value wins). After apply the parent shows the same result as the branch; recalculate the parent to refresh its calculations.', { title }),
        progress: t('Applying branch «{title}» to the parent model… please wait', { title }),
    }
}

// цель клика по числу: виджет строки метрики, иначе общий из настроек; '' — число не кликабельно
export function clickTarget(metric, attr) {
    const w = str(metric?.on_click_widget) || str(attr?.on_click?.widget)
    return w.trim()
}

// действие открытия отчёта под моделью карточки: сравнение с предыдущей моделью (ветка — с родителем) — путь compare_with_prev
export function openAction(widget, attr) {
    const cmp = attr?.on_click?.compare_with_prev !== false
    return { command: 'widget_open', widget, new: true, fieldsMapping: cmp ? [{ attr: { binding: 'doc.params.compare_with_prev', value: true } }] : [] }
}

// консоль: window.scorecard.get() / set(false) / reset() / stats() — поверх perf-flags стора
export const scStats = { renders: 0, clicks: 0, applies: 0, lastRenderMs: null }
export function installScorecardConsole(store, win = (typeof window !== 'undefined' ? window : null)) {
    if (!win || win.scorecard) return
    win.scorecard = {
        get: () => ({ cache: store.perf_flag(SC_FLAG_CACHE, true) }),
        set: (v) => { store.perf_flag(SC_FLAG_CACHE, true); if (win.perf) win.perf.set(SC_FLAG_CACHE, !!v); return win.scorecard.get() },
        reset: () => { store.perf_flag(SC_FLAG_CACHE, true); if (win.perf) win.perf.set(SC_FLAG_CACHE, true); return win.scorecard.get() },
        stats: () => ({ ...scStats }),
    }
}
